import {useState} from 'react';
import {DataTableSortStatus} from 'mantine-datatable';

import {useApplicationsInfiniteQuery} from './useApplicationsQuery';
import {useApplicationsSearch} from './useApplicationsSearch';
import {useApplicationsTableColumns} from './useApplicationsTableColumns';
import {Application} from '../types';
import {
    DEFAULT_SORT_STATUS,
    sortRecords,
} from '../utils/sortRecords';

const matchesKeyword = (item: Application, needle: string): boolean => {
    if (!needle) {
        return true;
    }
    const haystack = [
        item.name,
        item.description,
        item.url,
        item.applicationCategory,
        item.applicationStatus,
    ];
    return haystack.some(value => typeof value === 'string' && value.toLowerCase()
                                                                    .includes(needle));
};

export const useApplications = (filters: Record<string, string> = {}) => {
    const query = useApplicationsInfiniteQuery(filters);
    const {columns} = useApplicationsTableColumns();

    // Component state, NOT the module-scoped store: sort order is a property of
    // how ONE operator is looking at the table right now, unlike the keyword and
    // filters, which are shared across sibling components (FNP-815).
    const [sortStatus, setSortStatus] =
        useState<DataTableSortStatus<Application>>(DEFAULT_SORT_STATUS);

    // Keyword comes from the module-scoped store, so what is typed into the
    // toolbar in a SIBLING component actually reaches this filter (FNP-815).
    const {keyword} = useApplicationsSearch();

    // Pages are concatenated as they arrive. The type guard matters: a page can
    // be undefined mid-fetch, and a bare flatMap would throw. `'items' in page`
    // is the checklist's form (§9) — it narrows the page type rather than just
    // defaulting a possibly-absent property, so a page shaped differently by an
    // API change is dropped instead of contributing `undefined` rows.
    const items: Application[] = query.data?.pages?.flatMap(page =>
        (page && 'items' in page) ? page.items : []
    ) ?? [];

    const needle = keyword.trim()
                          .toLowerCase();

    // Sorting and keyword filtering are BOTH client-side, over the pages LOADED
    // SO FAR. Token pagination cannot reorder or match rows the browser has not
    // fetched — that is a property of the pagination model, not an oversight.
    const visibleItems = sortRecords(
        items.filter(item => matchesKeyword(item, needle)),
        sortStatus
    );

    return {
        items,
        visibleItems,
        columns,
        sortStatus,
        setSortStatus,
        loading: query.isLoading,
        isError: query.isError,
        // Distinct from `loading`: "fetching MORE" must not be rendered as the
        // initial load, or the already-loaded rows flash a spinner on every
        // scroll-to-bottom. The footer is the only thing that shows it.
        isFetchingNextPage: query.isFetchingNextPage,
        nextToken: query.hasNextPage ? 'has-more' : null,
        // Named to match the underlying react-query call and the reference
        // module (pipelines). The hasNextPage guard is the §9 box: calling
        // fetchNextPage with no next page re-requests the last one.
        fetchNextPage: () => {
            if (query.hasNextPage && !query.isFetchingNextPage) {
                query.fetchNextPage();
            }
        },
        reload: () => query.refetch(),
    };
};
